import React, { useState, useEffect } from 'react';
import useFormSubmit from '../hooks/useFormSubmit';
import { getMainCategories, getProductsBySubCategory } from '../data/priceList';
import './QuoteForm.css';

const QuoteForm = () => {
  const [activeTab, setActiveTab] = useState('simple'); // 'simple' or 'builder'
  const [mainCategories] = useState(getMainCategories());
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPackaging, setSelectedPackaging] = useState(null);
  // Removed unused quantity state
  const [quoteItems, setQuoteItems] = useState([]);
  
  // Simple form state
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    cableType: '',
    cableSize: '',
    coreType: '',
    quantity: '',
    additionalRequirements: ''
  });

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    notes: ''
  });

  const [showCustomerForm, setShowCustomerForm] = useState(false);

  const cableTypes = [
    'Copper Cable',
    'Aluminium Cable',
    'Armoured Cable',
    'Flexible Cable',
    'Welding Cable',
    'Submersible Cable',
    'AAC Conductor',
    'AAAC Conductor',
    'ACSR Conductor',
    'ABC Cable',
    'Control Cable',
    'Instrumentation Cable',
    'Domestic Cable',
    'Other'
  ];

  const coreTypes = [
    'Single Core',
    '2 Core',
    '3 Core',
    '4 Core',
    '5 Core',
    'Multi Core (specify in requirements)'
  ];

  // Your Google Apps Script Web App URL
  const sheetUrl = 'https://script.google.com/macros/s/AKfycbw5YlF2Ja0VziGv-CsSYV24wvOISL_Y0_ma4NkSFrdan-JjTcxbKqeNtJGcb_5jV4H3/exec';
  const { submitForm, isSubmitting, submitStatus } = useFormSubmit(sheetUrl);

  // Update products when subcategory changes
  useEffect(() => {
    if (selectedMainCategory && selectedSubCategory) {
      const catMap = {
        'cables': 'cables',
        'steel': 'steel',
        'pumps': 'pumps'
      };
      const mainCat = catMap[selectedMainCategory];
      const products = getProductsBySubCategory(mainCat, selectedSubCategory);
      setProducts(products);
    }
  }, [selectedMainCategory, selectedSubCategory]);

  const handleSimpleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleAddToQuote = () => {
    if (!selectedProduct || !selectedPackaging) return;

    const packaging = selectedProduct.packaging.find(p => 
      p.type === selectedPackaging.type && 
      p.length === selectedPackaging.length
    );

    setQuoteItems([
      ...quoteItems,
      {
        id: Date.now(),
        product: selectedProduct,
        packaging: selectedPackaging,
        quantity: 1,
        unitPrice: selectedProduct.unitPrice,
        totalPrice: packaging.price
      }
    ]);

    // Reset selections
    setSelectedProduct(null);
    setSelectedPackaging(null);
  };

  const handleRemoveItem = (id) => {
    setQuoteItems(quoteItems.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return quoteItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const handleSimpleSubmit = async (e) => {
    e.preventDefault();
    
    await submitForm({
      ...formData,
      formType: 'quote',
      quoteMethod: 'simple'
    });
    
    if (submitStatus === 'success') {
      setFormData({
        name: '',
        company: '',
        phone: '',
        email: '',
        cableType: '',
        cableSize: '',
        coreType: '',
        quantity: '',
        additionalRequirements: ''
      });
    }
  };

  const handleBuilderSubmit = async (e) => {
    e.preventDefault();
    
    const quoteData = {
      ...customerInfo,
      formType: 'quote',
      quoteMethod: 'builder',
      items: quoteItems.map(item => ({
        product: item.product.name,
        packaging: `${item.packaging.type} - ${item.packaging.length}${item.packaging.unit === 'meter' ? 'm' : ''}`,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice
      })),
      totalAmount: calculateTotal(),
      submittedAt: new Date().toISOString()
    };

    await submitForm(quoteData);
    
    if (submitStatus === 'success') {
      setQuoteItems([]);
      setCustomerInfo({
        name: '',
        company: '',
        email: '',
        phone: '',
        notes: ''
      });
      setShowCustomerForm(false);
    }
  };

  return (
    <div className="quote-form-container">
      {/* Tab Switcher */}
      <div className="quote-tabs">
        <button 
          className={`tab-btn ${activeTab === 'simple' ? 'active' : ''}`}
          onClick={() => setActiveTab('simple')}
        >
          Simple Quote
        </button>
        <button 
          className={`tab-btn ${activeTab === 'builder' ? 'active' : ''}`}
          onClick={() => setActiveTab('builder')}
        >
          Quote Builder
        </button>
      </div>

      {/* Simple Quote Tab */}
      {activeTab === 'simple' && (
        <div className="form-container quote-form">
          <h3>Request a Quote</h3>
          <p>Fill in the details below for a customized quote.</p>
          
          {submitStatus === 'success' && (
            <div className="form-message success">
              <span className="message-icon">✓</span>
              Thank you! Our sales team will contact you within 24 hours.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="form-message error">
              <span className="message-icon">⚠</span>
              Something went wrong. Please try again or call us directly.
            </div>
          )}

          <form onSubmit={handleSimpleSubmit} className="custom-form">
            <div className="form-section">
              <h4>Contact Information</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="Your Company Ltd."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="+254 7XX XXX XXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Cable Specifications</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cableType">Cable Type *</label>
                  <select
                    id="cableType"
                    name="cableType"
                    value={formData.cableType}
                    onChange={handleSimpleFormChange}
                    required
                  >
                    <option value="">Select Cable Type</option>
                    {cableTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="coreType">Core Type *</label>
                  <select
                    id="coreType"
                    name="coreType"
                    value={formData.coreType}
                    onChange={handleSimpleFormChange}
                    required
                  >
                    <option value="">Select Core Type</option>
                    {coreTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cableSize">Cable Size (mm²) *</label>
                  <input
                    type="text"
                    id="cableSize"
                    name="cableSize"
                    value={formData.cableSize}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="e.g., 2.5, 4, 6, 10, 16..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quantity">Quantity Required *</label>
                  <input
                    type="text"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleSimpleFormChange}
                    required
                    placeholder="e.g., 100 meters, 5 rolls"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="additionalRequirements">Additional Requirements</label>
                <textarea
                  id="additionalRequirements"
                  name="additionalRequirements"
                  value={formData.additionalRequirements}
                  onChange={handleSimpleFormChange}
                  rows="4"
                  placeholder="Any special requirements, standards, or specifications..."
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Request Quote'
              )}
            </button>
          </form>
        </div>
      )}

      {/* Quote Builder Tab */}
      {activeTab === 'builder' && (
        <div className="builder-container">
          <h3>Build Your Quote</h3>
          <p className="builder-subtitle">Select products from our price list. Our team will apply discounts and send you a final quote.</p>

          <div className="builder-layout">
            {/* Product Selection Panel */}
            <div className="selection-panel">
              <h4>1. Select Products</h4>
              
              {/* Main Category */}
              <div className="form-group">
                <label>Product Category</label>
                <select 
                  value={selectedMainCategory}
                  onChange={(e) => {
                    setSelectedMainCategory(e.target.value);
                    setSelectedSubCategory('');
                    setProducts([]);
                  }}
                >
                  <option value="">Select Category</option>
                  {mainCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Sub Category */}
              {selectedMainCategory && (
                <div className="form-group">
                  <label>Product Type</label>
                  <select 
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    {mainCategories
                      .find(c => c.id === selectedMainCategory)
                      ?.subCategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.icon} {sub.name}</option>
                      ))}
                  </select>
                </div>
              )}

              {/* Products List */}
              {selectedSubCategory && products.length > 0 && (
                <div className="products-list">
                  <label>Available Products</label>
                  {products.map(product => (
                    <div 
                      key={product.id}
                      className={`product-item ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                      onClick={() => setSelectedProduct(product)}
                    >
                      <div className="product-item-name">{product.name}</div>
                      <div className="product-item-price">KES {product.unitPrice.toLocaleString()} / {product.packaging[0]?.unit}</div>
                      {product.colors && <div className="product-item-colors">Colors: {product.colors.join(', ')}</div>}
                      {product.cores && <div className="product-item-specs">{product.cores}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Packaging Options */}
              {selectedProduct && (
                <div className="packaging-options">
                  <label>Packaging Options</label>
                  <div className="packaging-grid">
                    {selectedProduct.packaging.map((pkg, index) => (
                      <div
                        key={index}
                        className={`packaging-card ${selectedPackaging === pkg ? 'selected' : ''}`}
                        onClick={() => setSelectedPackaging(pkg)}
                      >
                        <div className="packaging-type">{pkg.type}</div>
                        <div className="packaging-details">
                          {pkg.length && <span>{pkg.length}{pkg.unit === 'meter' ? 'm' : ''}</span>}
                          {pkg.quantity && <span>{pkg.quantity} pieces</span>}
                        </div>
                        <div className="packaging-price">KES {pkg.price.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="btn-add-to-quote"
                    onClick={handleAddToQuote}
                    disabled={!selectedPackaging}
                  >
                    Add to Quote
                  </button>
                </div>
              )}
            </div>

            {/* Quote Summary Panel */}
            <div className="quote-summary-panel">
              <h4>2. Your Quote</h4>
              
              {quoteItems.length === 0 ? (
                <div className="empty-quote">
                  <p>No items added yet. Select products from the left panel.</p>
                </div>
              ) : (
                <>
                  <div className="quote-items">
                    {quoteItems.map(item => (
                      <div key={item.id} className="quote-item">
                        <div className="quote-item-details">
                          <h5>{item.product.name}</h5>
                          <p className="quote-item-packaging">
                            {item.packaging.type} - {item.packaging.length}{item.packaging.unit === 'meter' ? 'm' : ''}
                          </p>
                        </div>
                        <div className="quote-item-price">
                          <span>KES {item.totalPrice.toLocaleString()}</span>
                          <button 
                            className="remove-item"
                            onClick={() => handleRemoveItem(item.id)}
                          >×</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="quote-total">
                    <span>Total (excl. discounts):</span>
                    <span className="total-amount">KES {calculateTotal().toLocaleString()}</span>
                  </div>

                  {!showCustomerForm ? (
                    <button 
                      className="btn-proceed"
                      onClick={() => setShowCustomerForm(true)}
                    >
                      Proceed to Contact Details
                    </button>
                  ) : (
                    <div className="customer-form">
                      <h5>Your Contact Information</h5>
                      <div className="form-group">
                        <input
                          type="text"
                          name="name"
                          placeholder="Full Name *"
                          value={customerInfo.name}
                          onChange={handleCustomerInfoChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          name="company"
                          placeholder="Company Name"
                          value={customerInfo.company}
                          onChange={handleCustomerInfoChange}
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <input
                            type="email"
                            name="email"
                            placeholder="Email *"
                            value={customerInfo.email}
                            onChange={handleCustomerInfoChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Phone *"
                            value={customerInfo.phone}
                            onChange={handleCustomerInfoChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <textarea
                          name="notes"
                          placeholder="Additional notes or requirements..."
                          rows="3"
                          value={customerInfo.notes}
                          onChange={handleCustomerInfoChange}
                        />
                      </div>

                      <div className="discount-note">
                        <p>📧 Our team will review your quote and send you a final price with applicable discounts.</p>
                        <p className="discount-email">For urgent inquiries: <strong>masailink@masaigroup.co.ke</strong></p>
                      </div>

                      <button 
                        className="btn-submit-quote"
                        onClick={handleBuilderSubmit}
                        disabled={isSubmitting || !customerInfo.name || !customerInfo.email || !customerInfo.phone}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteForm;